import { NextPageContext } from 'next'
import { Request, Response } from 'express'
import { PCLNExpt } from '@pcln/experimentation'
import * as config from '../config/default.json'
import { PrePopHotels } from './utils/typeahead-fetch-types'

export type ResponseLocals = {
  footerHTML?: string
  headerHTML?: string
  webstats?: Webstats
  locale: any
  appInfo?: {
    appName?: string
    appVersion?: string
  }
  contentful: any
  customer?: {
    emailAddress?: string
    firstName?: string
  }
  seti?: BaseSetiResponseEvent[]
  signInStatus?: {
    signedIn: boolean
    passiveSignIn: boolean
  }
  partner?: Partner
  referral?: Referral
  features?: FeatureToggles
  pclnData: any
  additionalExperiments: any
  pclnExperimentation: PCLNExpt
  isDebugMode: boolean
  setiActiveTests: any
}

declare global {
  interface Window {
    PCLN?: {
      HeaderClient?: {
        openModal: () => void
      }
      pclnEventDispatcher?: {
        register: () => void
        unregister: () => void
        dispatch: (s: string) => void
      }
    }
    rudderanalytics: {
      load: (data: any) => void
      track: (name: any, data: any) => void
      page: (data: any) => void
      identify: () => void
      alias: () => void
      group: () => void
      ready: () => void
      reset: () => void
      getAnonymousId: () => string
      setAnonymousId: (guid: string) => void
    }
  }
}

export type Referral = {
  id?: string
  sourceId?: string
  clickId?: string
  webEntryTime?: string
  expiration?: string
  trafficSource?: string
  trafficTier?: number
}
export type Webstats = {
  resolvedClientIP: string
  clientGUID: string
  clientRequestId: string
  firstTimeHere: boolean
  requestId: string
  requestTime: number
  startOfVisit: boolean
  userAgent: string
  visitId: string
  isMobile: boolean
}

export interface BaseSetiResponseEvent {
  resultMessage: string
  setiVariantName: string
  setiVariantDesc: string
  setiTagName: string
  clientOverride: boolean
}

export interface SETIResponseEvent extends BaseSetiResponseEvent {
  resultCode: number
  setiID: number
  setiDesc: string
  setiTeamName: string
  createdByUser: string
  setiVariantID: number
  variantList: string[] | null
  assignmentFound: boolean
  assignmentRecorded: boolean
  impressionRecorded: boolean
  elapsedTime: string
  exception: Error | null
  experimentNotFound: boolean
  winningVariantFound: boolean
  offerRecorded: boolean
  experimentExpired: boolean
  experimentCreationQueued: boolean
  experimentNotNew: boolean
  usedConsistentHashing: boolean
  setiRunDateTime: string
}

export type TmsConfig = {
  productId: number
  pageType: string
  appCode: string
  mhe: string
  refId: string
  refClickId: string
  firstTimeHere: boolean
  startOfVisit: boolean
}

export type BootstrapData = {
  key: string
  referral: Referral
  webstats: Webstats
  appName: string
  appVersion: string
  baseUrl: string
  env: string
  isAndroid: boolean
  isCookieSetToHideAppBanner: boolean
  isIOS: boolean
  isMobile: boolean
  isTablet: boolean
  signInStatus: { signedIn: boolean; passiveSignIn: boolean }
  seti: Record<string, string>
  fullSETI: BaseSetiResponseEvent[]
  customer: {
    emailAddress: string
    firstName: string
  }
  locale: {
    localeCodeSource: string
    countryCode: string
    distanceFormat: unknown | null
    dateFormat: unknown | null
    countryName: string
    region: string
    city: string
    postalCode: string
    latitude: number
    longitude: number
    areaCode: number
    metroCode: number
    domainName: string
    orgName: string
    regionName: string
    timeZone: string
    ipAddr: string
    localeCode: string
    localeSupported: boolean
  }
  mostRecentBooking: {
    future: any
    current: any
  }
  partner: Partner | null
  featureToggles?: FeatureToggles
  insideTrackTitle: string
  prePopData?: {
    hotels: Partial<PrePopHotels>
  }
  contentful: any
}

export type CustomContext = NextPageContext & {
  req: Request
  res: Response<any, ResponseLocals>
}

type Product = {
  plfCode: string
  globalPartnerCode: string
  refid?: string
}

export type Partner = {
  isWLPartner?: boolean
  partnerCode: string
  pathCode?: string
  products: {
    hotel?: Product
    flight?: Product
    car?: Product
    package?: Product
  }
  config: {
    [propName: string]: any
  }
}

export type PageProps = {
  headerHTML: string
  footerHTML: string
  bootstrapData: BootstrapData
}

export type LOCATION_SEARCH_TYPE = {
  address?: string | null
  addressLine1?: string
  airportCode?: string | number | null
  airportName?: string
  aliases?: string[]
  cityCode?: string
  cityName: string | undefined
  content?: string
  country: string
  countryName?: string
  countryCode: string
  debitCardFlag?: string
  displayName?: string
  entered: string
  fromSavedSearch?: boolean
  gmtOffset?: number
  highlightedName: string | null
  javaTimeZoneName?: string
  id: string
  isoCountryCode?: string
  isRecent?: boolean
  itemName: string
  lang?: string
  lat?: number | undefined
  lon?: number | undefined
  majorAirportFlag?: string
  opaqueParticipantFlag?: string
  partnerCode?: string
  partnerLocationCode?: string
  partnerName?: string
  partnerShortName?: string
  phoneNumber?: string
  poiCategoryName?: string | null
  poiCategoryTypeId?: number
  poiID?: number | null
  postalCode?: string
  proximity?: number
  provinceName?: string | null
  radius?: number
  rank?: number | null
  rccAirportFlag?: string
  rentalLocationsCount?: number
  savedTravelStartDate?: string | null
  savedTravelEndDate?: string | null
  score?: number | string
  seType?: string | null
  stateCode: string | null
  timeZoneId?: number
  type: 'AIRPORT' | 'CITY' | 'POI' | 'HOTEL' | 'GEO' | 'LATLON' | 'PARTNER_LOC'
  zip?: string | null
  cityID: string
}

export type FeatureToggles = {
  [propName: string]: {
    [propName: string]: Record<string, unknown> | string | number | boolean
  }
}
export type EnumLiteralsOf<T extends Record<string, unknown>> = T[keyof T]

export const APP_ROUTES = {
  homepage: '/',
  flights: '/flights',
  hotels: '/hotels',
  vacationpackages: '/vacationpackages'
} as const

export type APP_ROUTES_TYPE = EnumLiteralsOf<typeof APP_ROUTES>

export type Configuration = typeof config

export type QueryString = { [key: string]: string | string[] }
